import { Trans } from '@lingui/react/macro';
import type { UserGroupRecordCustomer } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import type { BookingUserNotificationDto } from 'noddi-types';
import { NavioOverlayActions, NoddiBadge, NoddiButton, NoddiCheckbox } from 'noddi-ui-web';

import { useState } from 'react';
import { ApiErrorMessageWithTrans } from '../Error/ApiErrorMessageWithTrans';
import { NoddiCircularLoaderWithTrans } from '../Loading/NoddiCircularLoaderWithTrans';

type Props = {
  onConfirm: (notificationsToOverride: BookingUserNotificationDto[]) => void;
  userGroup: UserGroupRecordCustomer | undefined;
  userGroupId: number | undefined;
  bookedForUserId: number | undefined;
  confirmLabel?: React.ReactNode;
  driverUserIds?: ReadonlySet<number>;
  isConfirming?: boolean;
  licensePlateNumbers?: string[];
  /** Members the booker already turned SMS on for on the summary step. */
  preselectedUserIds?: ReadonlySet<number>;
};

export const NotificationSettingsDialog = ({
  onConfirm,
  userGroup,
  userGroupId,
  bookedForUserId,
  confirmLabel,
  driverUserIds: providedDriverUserIds,
  isConfirming = false,
  licensePlateNumbers,
  preselectedUserIds
}: Props) => {
  const hasCarContext = !!licensePlateNumbers?.length && !!userGroupId;
  // With the booked-for member the defaults already include the backend's
  // no-manager fallback, so the rows are exactly what create will notify.
  const {
    data: carDefaults,
    error: carDefaultsError,
    isFetching: isCarDefaultsFetching,
    isLoading: isCarDefaultsLoading,
    refetch: refetchCarDefaults
  } = noddiAsync.useGet({
    type: URLKeys.getCarNotificationDefaults,
    input: {
      userGroupId: userGroupId as number,
      licensePlateNumbers: licensePlateNumbers ?? [],
      bookedForUserId
    },
    queryConfig: { enabled: hasCarContext }
  });

  // Only track what the user has explicitly toggled — everything else comes from defaults.
  // Opt-ins made on the summary step count as toggles already made.
  const [userEdits, setUserEdits] = useState<Record<number, boolean>>(() =>
    Object.fromEntries([...(preselectedUserIds ?? [])].map((userId) => [userId, true]))
  );

  if (isCarDefaultsLoading) {
    return <NoddiCircularLoaderWithTrans />;
  }

  if (carDefaultsError) {
    return (
      <div className='flex flex-col gap-4'>
        <ApiErrorMessageWithTrans
          error={carDefaultsError}
          isRetrying={isCarDefaultsFetching}
          onRetry={() => void refetchCarDefaults()}
        />
        <NavioOverlayActions>
          <NoddiButton
            className='flex-1'
            variant='success'
            loading={isConfirming}
            disabled={isConfirming}
            onClick={() => onConfirm([])}
          >
            <Trans>Continue with default recipients</Trans>
          </NoddiButton>
        </NavioOverlayActions>
      </div>
    );
  }

  if (!userGroup) {
    return null;
  }

  const getNotificationForMember = (userId: number, groupLevelDefault: boolean): boolean => {
    if (userEdits[userId] !== undefined) {
      return userEdits[userId];
    }
    const carDefault = carDefaults?.find((d) => d.userId === userId);
    if (carDefault) {
      return carDefault.smsBookingNotifications;
    }
    return groupLevelDefault;
  };

  const handleToggle = (userId: number, checked: boolean) => {
    setUserEdits((prev) => ({ ...prev, [userId]: checked }));
  };

  const handleConfirm = () => {
    const notifications: BookingUserNotificationDto[] = userGroup.members.map((member) => ({
      userId: member.user.id,
      smsBookingNotifications: getNotificationForMember(member.user.id, member.smsBookingNotifications)
    }));
    onConfirm(notifications);
  };
  const driverUserIds =
    providedDriverUserIds ??
    new Set(carDefaults?.filter(({ isCarManager }) => isCarManager).map(({ userId }) => userId));

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-col gap-2'>
        {userGroup.members.map((member) => (
          <NoddiCheckbox
            key={member.user.id}
            id={`member-${member.user.id}`}
            checked={getNotificationForMember(member.user.id, member.smsBookingNotifications)}
            onCheckedChange={(checked) => handleToggle(member.user.id, checked)}
            label={
              <span className='flex items-center gap-2'>
                <span>{`${member.user.firstName} ${member.user.lastName}`}</span>
                {driverUserIds.has(member.user.id) && (
                  <NoddiBadge variant='outline'>
                    <Trans>Driver</Trans>
                  </NoddiBadge>
                )}
              </span>
            }
          />
        ))}
      </div>

      <NavioOverlayActions>
        <NoddiButton
          onClick={handleConfirm}
          className='w-full'
          variant='success'
          loading={isConfirming}
          disabled={isConfirming}
        >
          {confirmLabel ?? <Trans>Confirm booking</Trans>}
        </NoddiButton>
      </NavioOverlayActions>
    </div>
  );
};
